/**
 * Settings, as the old app had them: yourself, your sign-in, your
 * organisation, and (for a super admin) everyone.
 *
 * The tabs a person sees depend on what they can actually change, rather than
 * showing everything and refusing on click. A viewer gets their profile and
 * their sign-in and nothing else, because nothing else on this screen is
 * theirs to alter.
 */
import { Stack, Tabs, Title } from '@mantine/core';
import {
  IconArrowBackUp, IconBuilding, IconFileInvoice, IconFlame, IconForms, IconLayoutGrid, IconLock, IconUser,
  IconUserCircle, IconUsers, IconUsersGroup,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { ProfileTab } from './settings/ProfileTab';
import { SecurityTab } from './settings/SecurityTab';
import { OrganisationTab } from './settings/OrganisationTab';
import { UsersTab } from './settings/UsersTab';
import { PeopleSettingsTab } from './settings/PeopleSettingsTab';
import { TeamsTab } from './settings/TeamsTab';
import { PropertiesSettingsTab } from './properties/PropertiesSettingsTab';
import { ScoringSettingsTab } from './scoring/ScoringSettingsTab';
import { useScoringPermissions } from './scoring/scoringShared';
import { useCrmPermissions } from './crm/crmShared';
import { useVocab } from '../vocab';
import { ReturnsSettingsTab } from './settings/ReturnsSettingsTab';
import { BillingSettingsTab } from './settings/BillingSettingsTab';
import { TemplatesTab } from './settings/TemplatesTab';
import { useNavUser } from '../layout/navUser';
import { hasAction, isScopedUser } from '../layout/nav';

/** The Fields tab's key before it was renamed, kept so old links still open it. */
const LEGACY_FIELDS_TAB = 'properties';

export function SettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';
  const v = useVocab();
  // How submissions are linked into people: read by anyone who sees people,
  // changed by admins.
  const crm = useCrmPermissions();
  const people = crm.peopleLinking;
  // Teams and their rotations: manage_teams, and only while CRM is on.
  const teams = crm.manageTeams;
  // Fields (described with a label and type): manage_properties. With CRM
  // off, only the lead fields, which label what distribution reads.
  const fields = crm.manageProperties;
  // Scoring rules, bands and decay: manage_scoring. Kept with distribution
  // on as well as CRM, since routing can filter on a lead's score.
  const scoring = useScoringPermissions().manage;
  // Return reasons and window: manage_returns. Billing details (statements):
  // manage_billing.
  const navUser = useNavUser();
  const staff = !isScopedUser(navUser);
  const returnsTab = staff && hasAction(navUser, 'manage_returns');
  const billingTab = staff && hasAction(navUser, 'manage_billing');
  // Add a stream from a template: admins of the organisation itself.
  const templatesTab = isAdmin && staff;
  // ?tab= opens a tab directly, as the command bar does. One this person
  // does not have falls back to where they would land anyway.
  const [params, setParams] = useSearchParams();
  // Settings › Fields was Settings › Properties: an old link or bookmark
  // (?tab=properties) lands on Fields, and the address is rewritten so the
  // tab key people see from here on is the new one.
  useEffect(() => {
    if (params.get('tab') !== LEGACY_FIELDS_TAB) return;
    setParams((p) => { p.set('tab', 'fields'); return p; }, { replace: true });
  }, [params, setParams]);
  const allowed = [
    'profile', 'security', ...(isAdmin ? ['organisation'] : []), ...(templatesTab ? ['templates'] : []),
    ...(people ? ['people'] : []),
    ...(teams ? ['teams'] : []),
    ...(fields ? ['fields'] : []),
    ...(scoring ? ['scoring'] : []),
    ...(returnsTab ? ['returns'] : []),
    ...(billingTab ? ['billing'] : []),
    ...(isSuperAdmin ? ['users'] : []),
  ];
  const asked = params.get('tab');
  const wanted = asked === LEGACY_FIELDS_TAB ? 'fields' : asked;
  const tab = wanted && allowed.includes(wanted) ? wanted : isAdmin ? 'organisation' : 'profile';

  return (
    <Stack>
      <Title order={2}>Settings</Title>

      <Tabs
        value={tab}
        onChange={(next) => next && setParams((p) => { p.set('tab', next); return p; }, { replace: true })}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="profile" leftSection={<IconUser size={14} />}>
            My profile
          </Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<IconLock size={14} />}>
            Sign-in and security
          </Tabs.Tab>
          {isAdmin && (
            <Tabs.Tab value="organisation" leftSection={<IconBuilding size={14} />}>
              {isSuperAdmin ? 'Organisations' : 'Organisation'}
            </Tabs.Tab>
          )}
          {templatesTab && (
            <Tabs.Tab value="templates" leftSection={<IconLayoutGrid size={14} />}>
              Templates
            </Tabs.Tab>
          )}
          {people && (
            <Tabs.Tab value="people" leftSection={<IconUserCircle size={14} />}>
              {v.Many('person')}
            </Tabs.Tab>
          )}
          {teams && (
            <Tabs.Tab value="teams" leftSection={<IconUsersGroup size={14} />}>
              {v.Many('team')}
            </Tabs.Tab>
          )}
          {fields && (
            <Tabs.Tab value="fields" leftSection={<IconForms size={14} />}>
              {v.Many('property')}
            </Tabs.Tab>
          )}
          {scoring && (
            <Tabs.Tab value="scoring" leftSection={<IconFlame size={14} />}>
              Scoring
            </Tabs.Tab>
          )}
          {returnsTab && (
            <Tabs.Tab value="returns" leftSection={<IconArrowBackUp size={14} />}>
              Returns
            </Tabs.Tab>
          )}
          {billingTab && (
            <Tabs.Tab value="billing" leftSection={<IconFileInvoice size={14} />}>
              Billing
            </Tabs.Tab>
          )}
          {isSuperAdmin && (
            <Tabs.Tab value="users" leftSection={<IconUsers size={14} />}>
              Everyone
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="profile" pt="lg"><ProfileTab /></Tabs.Panel>
        <Tabs.Panel value="security" pt="lg"><SecurityTab /></Tabs.Panel>
        {isAdmin && <Tabs.Panel value="organisation" pt="lg"><OrganisationTab /></Tabs.Panel>}
        {templatesTab && <Tabs.Panel value="templates" pt="lg"><TemplatesTab /></Tabs.Panel>}
        {people && <Tabs.Panel value="people" pt="lg"><PeopleSettingsTab /></Tabs.Panel>}
        {teams && <Tabs.Panel value="teams" pt="lg"><TeamsTab /></Tabs.Panel>}
        {fields && <Tabs.Panel value="fields" pt="lg"><PropertiesSettingsTab /></Tabs.Panel>}
        {scoring && <Tabs.Panel value="scoring" pt="lg"><ScoringSettingsTab /></Tabs.Panel>}
        {returnsTab && <Tabs.Panel value="returns" pt="lg"><ReturnsSettingsTab /></Tabs.Panel>}
        {billingTab && <Tabs.Panel value="billing" pt="lg"><BillingSettingsTab /></Tabs.Panel>}
        {isSuperAdmin && <Tabs.Panel value="users" pt="lg"><UsersTab /></Tabs.Panel>}
      </Tabs>
    </Stack>
  );
}
